<template>
  <div class="SkillFormDialog">
    <v-dialog :value="dialog" persistent max-width="1000" @click:outside="$emit('close')">
      <v-card>
        <v-card-title class="text-h5">
          {{ isEditing ? 'Edit Skill' : 'Add New Skill' }}
        </v-card-title>

        <v-card-text>
          <v-form ref="form" v-model="valid">
            <!-- Skill Name -->
            <v-text-field
              v-model="formData.name"
              label="Skill Name"
              :rules="nameRules"
              required
              outlined
              dense
              class="mb-3"
              placeholder="e.g., PDF Analysis, Data Processing"
            ></v-text-field>

            <!-- Skill Description -->
            <v-textarea
              v-model="formData.description"
              label="Description"
              :rules="descriptionRules"
              required
              outlined
              dense
              rows="3"
              class="mb-3"
              placeholder="Brief description of what this skill does"
            ></v-textarea>

            <!-- Content Type Toggle -->
            <div class="SkillFormDialog__content-type-toggle mb-4">
              <v-btn-toggle
                v-model="contentType"
                mandatory
                dense
                class="mb-2"
              >
                <v-btn value="editor" small>
                  <v-icon left small>mdi-code-tags</v-icon>
                  Editor
                </v-btn>
                <v-btn value="file" small>
                  <v-icon left small>mdi-file-upload</v-icon>
                  Upload File
                </v-btn>
              </v-btn-toggle>
            </div>

            <!-- Monaco Editor for Markdown -->
            <div v-if="contentType === 'editor'" class="content-editor">
              <label class="SkillFormDialog__label">Skill Content (Markdown)</label>
              <div class="SkillFormDialog__editor-wrapper mt-2">
                <code-editor
                  v-model="formData.content"
                  language="markdown"
                  placeholder="Enter your skill content in Markdown format..."
                  height="300px"
                  @change="onContentChange"
                />
              </div>
            </div>

            <!-- File Upload -->
            <div v-else class="file-upload">
              <v-file-input
                v-model="selectedFile"
                label="Upload Markdown File"
                accept=".md,.markdown,.txt"
                outlined
                dense
                prepend-icon="mdi-paperclip"
                @change="onFileChange"
                :rules="fileRules"
              ></v-file-input>

              <!-- File Preview -->
              <div v-if="fileContent" class="SkillFormDialog__file-preview mt-3">
                <v-card outlined>
                  <v-card-subtitle>File Preview:</v-card-subtitle>
                  <v-card-text>
                    <pre class="SkillFormDialog__file-preview-content">{{ fileContent }}</pre>
                  </v-card-text>
                </v-card>
              </div>
            </div>

            <!-- Content Preview -->
            <div v-if="formData.content" class="SkillFormDialog__content-preview mt-4">
              <v-expansion-panels>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <span>
                      <v-icon left small>mdi-eye</v-icon>
                      Preview Content
                    </span>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="preview-content" v-html="renderedMarkdown"></div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </v-form>
        </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('close')">Cancel</v-btn>
        <v-btn
          color="primary"
          @click="save"
          :disabled="!valid || !formData.content"
          :loading="saving"
        >
          {{ isEditing ? 'Update' : 'Create' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </div>
</template>

<script>
import CodeEditor from '@/components/code-editor/code-editor.vue';
import markdownit from 'markdown-it';

export default {
  name: 'SkillFormDialog',
  components: {
    CodeEditor,
  },
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    skill: {
      type: Object,
      default: null,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      valid: false,
      saving: false,
      contentType: 'editor', // 'editor' or 'file'
      selectedFile: null,
      fileContent: '',
      formData: {
        name: '',
        description: '',
        content: '',
      },
      nameRules: [
        v => !!v || 'Name is required',
        v => (v && v.length >= 2) || 'Name must be at least 2 characters',
        v => (v && v.length <= 100) || 'Name must be less than 100 characters',
      ],
      descriptionRules: [
        v => !!v || 'Description is required',
        v => (v && v.length >= 10) || 'Description must be at least 10 characters',
        v => (v && v.length <= 500) || 'Description must be less than 500 characters',
      ],
      fileRules: [
        v => !v || v.size < 2000000 || 'File size should be less than 2 MB!',
      ],
    };
  },
  computed: {
    renderedMarkdown() {
      if (!this.formData.content) return '';
      try {
        return this.renderMarkdown(this.formData.content);
      } catch (error) {
        return '<p>Invalid markdown syntax</p>';
      }
    },
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        this.initializeForm();
      } else {
        this.resetForm();
      }
    },
    skill: {
      handler() {
        if (this.dialog) {
          this.initializeForm();
        }
      },
      deep: true,
    },
  },
  methods: {
    initializeForm() {
      if (this.isEditing && this.skill) {
        this.formData = {
          name: this.skill.name || '',
          description: this.skill.description || '',
          content: this.skill.content || '',
        };
      } else {
        this.resetForm();
      }
      this.contentType = 'editor';
      this.selectedFile = null;
      this.fileContent = '';
    },
    resetForm() {
      this.formData = {
        name: '',
        description: '',
        content: '',
      };
      this.contentType = 'editor';
      this.selectedFile = null;
      this.fileContent = '';
      this.valid = false;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    onContentChange(value) {
      this.formData.content = value;
    },
    async onFileChange(file) {
      if (!file) {
        this.fileContent = '';
        this.formData.content = '';
        return;
      }

      try {
        const text = await this.readFileAsText(file);
        this.fileContent = text;
        this.formData.content = text;
      } catch (error) {
        this.$store.dispatch('notifications/setError', 'Failed to read file');
        this.selectedFile = null;
      }
    },
    readFileAsText(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });
    },
    renderMarkdown(text) {
      const md = markdownit({
        breaks: true,
        linkify: true,
      });
      return md.render(text);
    },
    async save() {
      if (!this.$refs.form.validate()) {
        return;
      }

      if (!this.formData.content.trim()) {
        this.$store.dispatch('notifications/setError', 'Content is required');
        return;
      }

      this.saving = true;

      try {
        const skillData = {
          name: this.formData.name.trim(),
          description: this.formData.description.trim(),
          content: this.formData.content.trim(),
        };

        if (this.isEditing) {
          this.$emit('update', skillData);
        } else {
          this.$emit('save', skillData);
        }
      } catch (error) {
        this.$store.dispatch('notifications/setError', 'Failed to save skill');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './skill-form-dialog';
</style>